import React, { useState } from "react";
import { apiService } from "../services/api";
import {
  Project,
  CreateInvitationRequest,
} from "../types/api";
import { InvitationPreviewModal } from "./InvitationPreviewModal";

interface InviteUserWidgetProps {
  projects: Project[];
  inviterName: string;
  inviterEmail: string;
  onInvited?: () => void;
}

export const InviteUserWidget: React.FC<InviteUserWidgetProps> = ({
  projects,
  inviterName,
  inviterEmail,
  onInvited,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [projectId, setProjectId] = useState<number | "">(
    projects.length > 0 ? projects[0].id : ""
  );
  const [role, setRole] = useState("researcher");
  const [expiry, setExpiry] = useState(168);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const selectedProject = projects.find((p) => p.id === projectId);

  const reset = () => {
    setEmail("");
    setProjectId(projects.length > 0 ? projects[0].id : "");
    setRole("researcher");
    setExpiry(168);
  };

  const handleInvite = () => {
    if (!isValidEmail || !selectedProject) return;
    setShowPreviewModal(true);
  };

  const handleInviteConfirm = async (customMessage: string) => {
    if (!selectedProject) return;
    setShowPreviewModal(false);
    try {
      setActionLoading(true);
      const request: CreateInvitationRequest = {
        email: email.trim(),
        role,
        expires_in_hours: expiry,
        custom_message: customMessage || undefined,
      };
      await apiService.createInvitation(selectedProject.id, request);
      setMessage({
        type: "success",
        text: `Invitation sent to ${email.trim()} for project "${selectedProject.title}".`,
      });
      reset();
      onInvited?.();
    } catch (err) {
      setMessage({
        type: "error",
        text: err instanceof Error ? err.message : "Failed to send invitation.",
      });
    } finally {
      setActionLoading(false);
    }
  };

  React.useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  if (projects.length === 0) {
    return null;
  }

  if (!isOpen) {
    return (
      <div style={{ marginBottom: "16px" }}>
        <button
          onClick={() => setIsOpen(true)}
          style={{
            padding: "9px 16px",
            background: "var(--fill-primary)",
            color: "var(--on-primary)",
            border: "none",
            borderRadius: "var(--radius)",
            cursor: "pointer",
            fontWeight: 500,
            fontSize: "14px",
            fontFamily: "inherit",
          }}
        >
          + Invite User
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        background: "#f8f9fa",
        padding: "16px",
        borderRadius: "8px",
        marginBottom: "16px",
        border: "1px solid #dee2e6",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "12px",
        }}
      >
        <h4 style={{ margin: 0 }}>Invite User to a Project</h4>
        <button
          onClick={() => {
            setIsOpen(false);
            reset();
          }}
          style={{
            padding: "4px 12px",
            background: "#6c757d",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "13px",
          }}
        >
          Close
        </button>
      </div>

      {message && (
        <div
          style={{
            padding: "8px 12px",
            borderRadius: "4px",
            marginBottom: "12px",
            background: message.type === "success" ? "#d4edda" : "#f8d7da",
            color: message.type === "success" ? "#155724" : "#721c24",
            border: `1px solid ${message.type === "success" ? "#c3e6cb" : "#f5c6cb"}`,
            fontSize: "14px",
          }}
        >
          {message.text}
        </div>
      )}

      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "end" }}>
        <div style={{ flex: "1 1 240px", minWidth: "200px" }}>
          <label style={{ display: "block", marginBottom: "4px", fontWeight: 500, fontSize: "13px" }}>
            Email:
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            disabled={actionLoading}
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #dee2e6",
              borderRadius: "4px",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          />
        </div>

        <div style={{ flex: "1 1 220px", minWidth: "200px" }}>
          <label style={{ display: "block", marginBottom: "4px", fontWeight: 500, fontSize: "13px" }}>
            Project:
          </label>
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value ? parseInt(e.target.value) : "")}
            disabled={actionLoading}
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #dee2e6",
              borderRadius: "4px",
              boxSizing: "border-box",
            }}
          >
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </select>
        </div>

        <div style={{ width: "160px" }}>
          <label style={{ display: "block", marginBottom: "4px", fontWeight: 500, fontSize: "13px" }}>
            Role:
          </label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            disabled={actionLoading}
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #dee2e6",
              borderRadius: "4px",
              boxSizing: "border-box",
            }}
          >
            <option value="researcher">Researcher</option>
            <option value="pi">PI (Principal Investigator)</option>
          </select>
        </div>

        <div style={{ width: "180px" }}>
          <label style={{ display: "block", marginBottom: "4px", fontWeight: 500, fontSize: "13px" }}>
            Expires in:
          </label>
          <select
            value={expiry}
            onChange={(e) => setExpiry(parseInt(e.target.value))}
            disabled={actionLoading}
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #dee2e6",
              borderRadius: "4px",
              boxSizing: "border-box",
            }}
          >
            <option value={24}>24 hours (1 day)</option>
            <option value={72}>72 hours (3 days)</option>
            <option value={168}>168 hours (7 days)</option>
            <option value={336}>336 hours (14 days)</option>
            <option value={720}>720 hours (30 days)</option>
          </select>
        </div>

        <button
          onClick={handleInvite}
          disabled={actionLoading || !isValidEmail || !selectedProject}
          style={{
            padding: "8px 20px",
            background: actionLoading || !isValidEmail || !selectedProject ? "#adb5bd" : "#28a745",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: actionLoading || !isValidEmail || !selectedProject ? "not-allowed" : "pointer",
            fontWeight: 500,
          }}
        >
          {actionLoading ? "Sending..." : "Send Invitation"}
        </button>
      </div>

      {showPreviewModal && selectedProject && (
        <InvitationPreviewModal
          recipientEmail={email.trim()}
          defaultMessage=""
          inviterName={inviterName}
          inviterEmail={inviterEmail}
          systemEmail="nafnleyndarkerfi@hi.is"
          inviteType="project"
          projectTitle={selectedProject.title}
          onSend={handleInviteConfirm}
          onCancel={() => setShowPreviewModal(false)}
        />
      )}
    </div>
  );
};
